import { FC, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { format } from 'date-fns';
import DateRangeDropdown from '../lpiReports/DateRangeDropdown';
import './HocDatasetTab.scss';

interface SessionTokens {
  jwtToken: string;
  sessionId: string;
}

interface HocDatasetTabProps {
  getSessionTokensFn: () => Promise<SessionTokens>;
}

interface HocRecord {
  hocRecordId: string;
  hocIdentificatie: string;
  hocType: string | null;
  plaats: string | null;
  startdatum: string;
  einddatum: string;
  energiedrager: string | null;
  hubEmissionSource: string | null;
  computedHubWtwFactor: number | null;
  createdFromFile: string | null;
  lastUpdatedFromFile: string | null;
}

const PAGE_SIZE = 50;

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  return iso.slice(0, 10);
};

const formatFactor = (value: number | null): string => {
  if (value == null) return '—';
  return value.toLocaleString(undefined, { maximumFractionDigits: 4 });
};

const HocDatasetTab: FC<HocDatasetTabProps> = ({ getSessionTokensFn }) => {
  const { t } = useTranslation();

  const [records, setRecords] = useState<HocRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  // Committed dates only update on Apply, so the date picker doesn't fetch mid-selection.
  const [committedStartDate, setCommittedStartDate] = useState<Date | null>(null);
  const [committedEndDate, setCommittedEndDate] = useState<Date | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchRecords = useCallback(async () => {
    setIsLoading(true);
    setFetchError(null);
    try {
      const { jwtToken, sessionId } = await getSessionTokensFn();
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String((page - 1) * PAGE_SIZE),
      });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (committedStartDate) params.set('periodStart', format(committedStartDate, 'yyyy-MM-dd'));
      if (committedEndDate) params.set('periodEnd', format(committedEndDate, 'yyyy-MM-dd'));

      const res = await axios.get<{ hocs: HocRecord[]; total: number }>(
        `${process.env.REACT_APP_LPI_API_URL}/cpi/hoc?${params}`,
        { headers: { Authorization: `Session ${jwtToken}`, 'X-Session-Id': sessionId } }
      );
      setRecords(res.data.hocs ?? []);
      setTotal(res.data.total ?? 0);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error?.message ||
        err?.message ||
        'Failed to load hubs of consolidation.';
      setFetchError(msg);
    } finally {
      setIsLoading(false);
    }
  }, [page, debouncedSearch, committedStartDate, committedEndDate, getSessionTokensFn]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="hoc-dataset">
      <div className="hoc-dataset__header">
        <div>
          <h1>{t('HOC data')}</h1>
          <p className="hoc-dataset__subtitle">
            {t('Hubs of consolidation ingested from HOC files.')}
          </p>
        </div>
      </div>

      <div className="hoc-dataset__controls">
        <DateRangeDropdown
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          isLoading={isLoading}
        />
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            setCommittedStartDate(startDate);
            setCommittedEndDate(endDate);
            setPage(1);
          }}
          disabled={!startDate || !endDate}
        >
          {t('Apply')}
        </button>
      </div>

      <div className="hoc-dataset__card">
        <div className="hoc-dataset__toolbar">
          <div className="hoc-dataset__filters">
            <input
              type="text"
              className="hoc-dataset__search-input"
              placeholder={t('Search by hub or location…')}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
          {!isLoading && (
            <span className="hoc-dataset__count">
              {total} {total === 1 ? t('record') : t('records')}
            </span>
          )}
        </div>

        {fetchError && <div className="hoc-dataset__error">{fetchError}</div>}

        {!fetchError && (
          <>
            <div
              className={`hoc-dataset__table-wrap${
                isLoading ? ' hoc-dataset__table-wrap--loading' : ''
              }`}
            >
              <table className="hoc-dataset__table">
                <thead>
                  <tr>
                    <th>{t('HOC ID')}</th>
                    <th>{t('Type')}</th>
                    <th>{t('Location')}</th>
                    <th>{t('Start date')}</th>
                    <th>{t('End date')}</th>
                    <th>{t('Energy carrier')}</th>
                    <th>{t('Emission source')}</th>
                    <th>{t('Hub factor (WTW)')}</th>
                    <th>{t('Source file')}</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading && (
                    <tr>
                      <td colSpan={9} className="hoc-dataset__cell--center">
                        {t('Loading…')}
                      </td>
                    </tr>
                  )}
                  {!isLoading && records.length === 0 && (
                    <tr>
                      <td colSpan={9} className="hoc-dataset__cell--center">
                        {t('No hubs of consolidation found for the selected filters.')}
                      </td>
                    </tr>
                  )}
                  {!isLoading &&
                    records.map((r) => {
                      const sourceFile = r.lastUpdatedFromFile ?? r.createdFromFile;
                      return (
                        <tr key={r.hocRecordId}>
                          <td className="hoc-dataset__cell--id">{r.hocIdentificatie}</td>
                          <td>{r.hocType ?? '—'}</td>
                          <td>{r.plaats ?? '—'}</td>
                          <td>{formatDate(r.startdatum)}</td>
                          <td>{formatDate(r.einddatum)}</td>
                          <td>{r.energiedrager || '—'}</td>
                          <td>{r.hubEmissionSource ?? '—'}</td>
                          <td>{formatFactor(r.computedHubWtwFactor)}</td>
                          <td>
                            <span className="hoc-dataset__file" title={sourceFile ?? ''}>
                              {sourceFile ?? '—'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="hoc-dataset__pagination">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setPage(1)}
                  disabled={page === 1 || isLoading}
                  aria-label={t("First page")}
                >
                  «
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setPage((p) => p - 1)}
                  disabled={page === 1 || isLoading}
                  aria-label={t("Previous page")}
                >
                  ‹
                </button>
                <span className="hoc-dataset__page-info">
                  {t('Page')} {page} {t('of')} {totalPages}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page === totalPages || isLoading}
                  aria-label={t("Next page")}
                >
                  ›
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setPage(totalPages)}
                  disabled={page === totalPages || isLoading}
                  aria-label={t("Last page")}
                >
                  »
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default HocDatasetTab;
